import ConstraintsConfig from 'config/constraints-config.json';

type ConfigSection = {
  type: string;
  label?: string;
  data?: Record<string, { label?: string }>;
};

const SECTIONS: ConfigSection[] = (ConstraintsConfig as { constraints: ConfigSection[] }).constraints;

const SECTION_BY_TYPE: Record<string, ConfigSection> = (() => {
  const map: Record<string, ConfigSection> = {};
  for (const s of SECTIONS) map[s.type] = s;
  return map;
})();

/** Resolve a panel section's heading. Falls back to the section key when missing. */
export function sectionLabel(sectionKey: string): string {
  return SECTION_BY_TYPE[sectionKey]?.label ?? sectionKey;
}

/**
 * Resolve a panel field's label exactly the way each panel section component
 * does (e.g. AssetContent, EsgContent receive `configData` = the slice of
 * `constraints-config.json` for their panel section, then read
 * `configData.data[fieldName].label`).
 *
 * Resolution order:
 * 1. i18n key `constraints.<fieldName>` if `t()` is supplied and returns
 *    something other than the key itself (covers fields like `risk_profile`
 *    that the pane labels through i18n, not the config).
 * 2. `constraints-config.json[sectionKey].data[fieldName].label`.
 * 3. The raw fieldName.
 *
 * Callers MUST pass the PANEL SECTION key (e.g. "asset"), not the real
 * Document group (e.g. "instrument_type") — the JSON nests labels by panel
 * section, not by Document group.
 */
export function fieldLabel(
  sectionKey: string,
  fieldName: string,
  t?: (key: string) => string,
): string {
  if (t) {
    const i18nKey = `constraints.${fieldName}`;
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }
  return SECTION_BY_TYPE[sectionKey]?.data?.[fieldName]?.label ?? fieldName;
}

/**
 * Resolve a field's position within its panel section's `data` block. Used
 * to sort recap bullets so they appear in the same order as the pane (which
 * iterates `data` in JSON declaration order). Returns
 * Number.MAX_SAFE_INTEGER when the field is unknown so the sort stays
 * stable for outliers.
 *
 * Like `fieldLabel`, requires the PANEL SECTION key.
 */
export function fieldOrder(sectionKey: string, fieldName: string): number {
  const data = SECTION_BY_TYPE[sectionKey]?.data;
  if (!data) return Number.MAX_SAFE_INTEGER;
  const idx = Object.keys(data).indexOf(fieldName);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
}
